import type { VillageReferencePayload, VillageReferenceRecord } from "../domain/contracts";
import { frontendEnv } from "./env";
import { fetchJson } from "./apiClient";

const referenceAliasBySlug = new Map<string, string>([
  ["kha-maung", "ah-shey-kha-maung-seik"],
  ["kha-mway-chaung", "pe-lun-kha-mway"],
  ["kun-taung", "kyun-taung"],
  ["myo", "maungdaw-town"],
  ["myo-thu-gyi-yar-zar-bi", "maungdaw-town"],
  ["shwe-zar-kat-pa-kaung", "kat-pa-kaung"],
]);

function normalizeLookupValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function logReferenceSource(source: "api" | "static-fallback", count: number): void {
  if (typeof window !== "undefined") {
    console.info(`[village-reference] source=${source} count=${count}`);
  }
}

export async function fetchVillageReference(): Promise<VillageReferenceRecord[]> {
  const apiUrl = `${frontendEnv.publicApiBaseUrl}/api/reference/villages`;

  try {
    const payload = await fetchJson<VillageReferencePayload>(apiUrl);
    const apiItems = Array.isArray(payload.items) ? payload.items : [];
    if (apiItems.length > 0) {
      logReferenceSource("api", apiItems.length);
      return apiItems;
    }
  } catch (error) {
    if (typeof window !== "undefined") {
      const message = error instanceof Error ? error.message : String(error);
      console.warn(`[village-reference] api unavailable, using static fallback: ${message}`);
    }
  }

  const payload = await fetchJson<VillageReferencePayload>(frontendEnv.referenceJsonPath);
  const fallbackItems = Array.isArray(payload.items) ? payload.items : [];
  logReferenceSource("static-fallback", fallbackItems.length);
  return fallbackItems;
}

export function matchReferenceRecord(villageName: string, slug: string, reference: VillageReferenceRecord[]): VillageReferenceRecord | null {
  const normalizedSlug = normalizeLookupValue(slug);
  const normalizedName = normalizeLookupValue(villageName);

  const bySlug = reference.find((item) => item.slug === slug || item.slug === normalizedSlug);
  if (bySlug) {
    return bySlug;
  }

  const byTitle = reference.find((item) => normalizeLookupValue(item.title) === normalizedName);
  if (byTitle) {
    return byTitle;
  }

  const aliasSlug = referenceAliasBySlug.get(normalizedSlug) ?? referenceAliasBySlug.get(normalizedName);
  if (!aliasSlug) {
    return null;
  }

  return reference.find((item) => item.slug === aliasSlug) ?? null;
}